import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Plus, Bank, CheckCircle, XCircle, ArrowCounterClockwise, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_TONE = {
  client: "bg-blue-50 text-blue-700 border-blue-200",
  donor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  investor: "bg-violet-50 text-violet-700 border-violet-200",
  bank: "bg-slate-100 text-slate-700 border-slate-300",
  partner: "bg-amber-50 text-amber-700 border-amber-200",
  government: "bg-cyan-50 text-cyan-700 border-cyan-200",
  other: "bg-slate-50 text-slate-500 border-slate-200",
};

const STATUS_META = {
  pending_review: { label: "Awaiting review", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  reviewed: { label: "Awaiting activation", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  active: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", tone: "bg-red-50 text-red-700 border-red-200" },
  returned: { label: "Returned", tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

const ROLE_RANK = {
  staff: 1, finance_officer: 1, hr_officer: 1, procurement_officer: 1,
  compliance_officer: 2, asset_manager: 2, finance_manager: 2, hr_manager: 2,
  procurement_manager: 2, project_manager: 2, manager: 2,
  management: 3, admin: 3, super_admin: 4,
};

function CreateFunderDialog({ open, onClose, onCreated, types }) {
  const empty = {
    name: "", funder_type: "client", country: "Somalia", contact_person: "", contact_email: "",
    contact_phone: "", relationship: "",
    banking: { bank_name: "", account_name: "", account_number: "", swift: "", currency: "USD" },
    due_diligence: { registration_no: "", tax_id: "", sanctions_check: "", notes: "" },
    reporting: { frequency: "", format: "", requirements: "" },
    compliance_notes: "",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setForm(empty); }, [open]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (g, k, v) => setForm((f) => ({ ...f, [g]: { ...f[g], [k]: v } }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/funders", form);
      toast.success(`${data.funder_no} registered — awaiting combined Finance/Compliance review`);
      onCreated(data); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Register a funder</DialogTitle>
          <DialogDescription>Essentials first — expand the sections below only if you have the details now.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Funder name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Organisation name" data-testid="funder-name" />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.funder_type} onValueChange={(v) => set("funder_type", v)}>
                <SelectTrigger data-testid="funder-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(types || []).map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="funder-country" />
            </div>
            <div>
              <Label>Contact person</Label>
              <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="funder-contact" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="funder-email" />
            </div>
            <div className="col-span-2">
              <Label>Funding relationship</Label>
              <Textarea rows={2} value={form.relationship} onChange={(e) => set("relationship", e.target.value)}
                        placeholder="e.g. Client for supervision contracts; donor for WASH programme" data-testid="funder-relationship" />
            </div>
          </div>

          <Accordion type="multiple" className="w-full">
            <AccordionItem value="banking">
              <AccordionTrigger className="text-xs font-semibold" data-testid="funder-acc-banking">Banking details (optional)</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Bank name" value={form.banking.bank_name} onChange={(e) => setNested("banking", "bank_name", e.target.value)} />
                  <Input placeholder="Account name" value={form.banking.account_name} onChange={(e) => setNested("banking", "account_name", e.target.value)} />
                  <Input placeholder="Account number / IBAN" value={form.banking.account_number} onChange={(e) => setNested("banking", "account_number", e.target.value)} />
                  <Input placeholder="SWIFT / BIC" value={form.banking.swift} onChange={(e) => setNested("banking", "swift", e.target.value)} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="dd">
              <AccordionTrigger className="text-xs font-semibold" data-testid="funder-acc-dd">Due diligence (optional)</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Registration no." value={form.due_diligence.registration_no} onChange={(e) => setNested("due_diligence", "registration_no", e.target.value)} />
                  <Input placeholder="Tax ID" value={form.due_diligence.tax_id} onChange={(e) => setNested("due_diligence", "tax_id", e.target.value)} />
                  <Input className="col-span-2" placeholder="Sanctions / KYC check reference" value={form.due_diligence.sanctions_check} onChange={(e) => setNested("due_diligence", "sanctions_check", e.target.value)} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="reporting">
              <AccordionTrigger className="text-xs font-semibold" data-testid="funder-acc-reporting">Reporting requirements (optional)</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Frequency (e.g. quarterly)" value={form.reporting.frequency} onChange={(e) => setNested("reporting", "frequency", e.target.value)} />
                  <Input placeholder="Format (e.g. donor template)" value={form.reporting.format} onChange={(e) => setNested("reporting", "format", e.target.value)} />
                  <Textarea className="col-span-2" rows={2} placeholder="Specific requirements" value={form.reporting.requirements} onChange={(e) => setNested("reporting", "requirements", e.target.value)} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name.trim()} data-testid="funder-submit">
            {busy ? "Registering…" : "Register funder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FunderDrawer({ funderId, onClose, onChanged, canApprove }) {
  const [f, setF] = useState(null);
  const [projects, setProjects] = useState([]);
  const [linking, setLinking] = useState("");
  const load = () => api.get(`/funders/${funderId}`).then((r) => setF(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data.items || r.data || [])).catch(() => {});
  }, [funderId]); // eslint-disable-line

  const decide = async (decision) => {
    const note = decision !== "approve" ? window.prompt(`Reason for ${decision}:`) : "";
    if (decision !== "approve" && note === null) return;
    try {
      const { data } = await api.post(`/funders/${funderId}/decide`, { decision, note: note || "" });
      toast.success(`Funder ${data.status}`);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const link = async () => {
    if (!linking) return;
    try {
      await api.post(`/funders/${funderId}/link-project`, { project_id: linking });
      toast.success("Project linked"); setLinking(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!f) return null;
  const meta = STATUS_META[f.status] || STATUS_META.pending_review;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="funder-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bank size={18} className="text-[#1E5CBA]" /> {f.name}
          </SheetTitle>
          <SheetDescription>
            <span className="font-mono-alt text-[10px]">{f.funder_no}</span>
            <span className={`ml-2 inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${TYPE_TONE[f.funder_type] || TYPE_TONE.other}`}>{f.funder_type}</span>
            <span className={`ml-1.5 inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.tone}`} data-testid="funder-drawer-status">{meta.label}</span>
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          {(f.status === "pending_review" || f.status === "reviewed") && canApprove && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
              <div className="text-xs font-semibold text-blue-900">
                {f.status === "pending_review" ? "Step 1 of 2 — combined Finance / Compliance review" : "Step 2 of 2 — manager activation"}
              </div>
              {f.status === "reviewed" && f.review && (
                <div className="mt-0.5 text-[11px] text-blue-800">Reviewed by {f.review.reviewed_by} · {formatDate(f.review.reviewed_at)}</div>
              )}
              <div className="mt-2 flex gap-2">
                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => decide("approve")}
                        data-testid="funder-approve-btn">
                  <CheckCircle size={12} className="mr-1" /> {f.status === "pending_review" ? "Approve review" : "Activate funder"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => decide("return")} data-testid="funder-return-btn">
                  <ArrowCounterClockwise size={12} className="mr-1" /> Return
                </Button>
                <Button size="sm" variant="outline" className="border-red-200 text-red-600" onClick={() => decide("reject")}
                        data-testid="funder-reject-btn">
                  <XCircle size={12} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <div><span className="text-slate-500">Country</span><div className="font-medium">{f.country || "—"}</div></div>
            <div><span className="text-slate-500">Contact</span><div className="font-medium">{f.contact_person || "—"}</div></div>
            <div><span className="text-slate-500">Email</span><div className="font-medium">{f.contact_email || "—"}</div></div>
            <div><span className="text-slate-500">Phone</span><div className="font-medium">{f.contact_phone || "—"}</div></div>
            <div className="col-span-2"><span className="text-slate-500">Relationship</span><div className="font-medium">{f.relationship || "—"}</div></div>
          </div>

          {(f.banking?.bank_name || f.banking?.account_number) && (
            <div className="rounded-md border border-slate-200 p-3 text-xs">
              <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 mb-1">Banking</div>
              {f.banking.bank_name} · {f.banking.account_name} · {f.banking.account_number} {f.banking.swift && `· ${f.banking.swift}`}
            </div>
          )}
          {(f.due_diligence?.registration_no || f.due_diligence?.tax_id) && (
            <div className="rounded-md border border-slate-200 p-3 text-xs">
              <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 mb-1">Due diligence</div>
              Reg: {f.due_diligence.registration_no || "—"} · Tax: {f.due_diligence.tax_id || "—"}
              {f.due_diligence.sanctions_check && ` · KYC: ${f.due_diligence.sanctions_check}`}
            </div>
          )}
          {(f.reporting?.frequency || f.reporting?.requirements) && (
            <div className="rounded-md border border-slate-200 p-3 text-xs">
              <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 mb-1">Reporting</div>
              {f.reporting.frequency && <span>{f.reporting.frequency} · </span>}{f.reporting.format}{f.reporting.requirements && ` — ${f.reporting.requirements}`}
            </div>
          )}

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Linked projects ({(f.projects || []).length})</div>
            </div>
            {(f.projects || []).length === 0
              ? <div className="text-xs text-slate-400">No projects linked yet.</div>
              : (
                <ul className="space-y-1">
                  {f.projects.map((p) => (
                    <li key={p.id} className="flex items-center gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs">
                      <span className="flex-1 font-medium">{p.name}</span>
                      <span className="text-slate-500">{formatCurrency(p.budget)}</span>
                      <span className="rounded bg-white border border-slate-200 px-1 text-[9px] uppercase">{p.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            {canApprove && (
              <div className="mt-2 flex gap-2">
                <Select value={linking || "none"} onValueChange={(v) => setLinking(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="funder-link-select"><SelectValue placeholder="Link a project…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Link a project…</SelectItem>
                    {projects.filter((p) => !(f.projects || []).some((x) => x.id === p.id)).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8" onClick={link} disabled={!linking} data-testid="funder-link-btn">
                  <LinkSimple size={12} className="mr-1" /> Link
                </Button>
              </div>
            )}
          </div>

          <div>
            <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 mb-1">History</div>
            <ul className="space-y-0.5">
              {(f.trail || []).map((t, i) => (
                <li key={i} className="flex gap-2 text-[11px] text-slate-600">
                  <span className="font-mono-alt text-slate-400">{formatDate(t.at)}</span>
                  <span className="font-medium">{t.action}</span>
                  <span className="text-slate-400">{t.by}</span>
                  {t.note && <span className="italic">"{t.note}"</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Funders() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);

  const canApprove = (ROLE_RANK[user?.role] || 1) >= 2;

  const load = () => api.get("/funders", { params: { q, funder_type: typeFilter === "all" ? "" : typeFilter } })
    .then((r) => setRows(r.data.items || []))
    .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [typeFilter]); // eslint-disable-line
  useEffect(() => { api.get("/funders/meta").then((r) => setTypes(r.data.types || [])).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-6" data-testid="funders-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900">Funders</h1>
          <p className="text-xs text-slate-500">One central register for clients, donors, investors, banks & partners · 2-step approval (review → activate)</p>
        </div>
        <Button className="ml-auto btn-primary-blue" onClick={() => setShowNew(true)} data-testid="funder-new-btn">
          <Plus size={14} className="mr-1" /> Register funder
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search name / number…" value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()} className="h-8 w-56 text-xs" data-testid="funder-search" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="funder-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">No</th><th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Country</th>
              <th className="px-4 py-2.5">Contact</th><th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-slate-400" data-testid="funders-empty">
                No funders registered yet.
              </td></tr>
            )}
            {rows.map((f) => {
              const meta = STATUS_META[f.status] || STATUS_META.pending_review;
              return (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(f.id)}
                    data-testid={`funder-row-${f.funder_no}`}>
                  <td className="px-4 py-2.5 font-mono-alt text-xs">{f.funder_no}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{f.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${TYPE_TONE[f.funder_type] || TYPE_TONE.other}`}>{f.funder_type}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{f.country || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{f.contact_person || f.contact_email || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.tone}`}
                          data-testid={`funder-status-${f.funder_no}`}>{meta.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateFunderDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => load()} types={types} />
      {openId && <FunderDrawer funderId={openId} onClose={() => setOpenId(null)} onChanged={load} canApprove={canApprove} />}
    </div>
  );
}
